import { useState, useEffect, useRef, useCallback } from 'react';
import { learningSubjects, Subject, Problem } from '../../../mocks/learningProblems';
import { computeLocally, ComputeResult } from '../mathEngine';
import MathRenderer from '../../../components/base/MathRenderer';

// ─── Types ────────────────────────────────────────────────────────────────────

type Phase = 'setup' | 'running' | 'review';

interface AnswerRecord {
  problemId: string;
  userInput: string;
  userResult: ComputeResult | null;
  expectedResult: ComputeResult | null;
  isCorrect: boolean;
  skipped: boolean;
  timeTakenSec: number;
}

interface ExamConfig {
  subject: Subject;
  problems: Problem[];
  timeLimitSec: number; // 0 = unlimited
}

interface ExamModeProps {
  onClose: () => void;
  onComplete?: (wrongAnswers: { statement: string; userAnswer: string; correctAnswer: string; subject: string }[]) => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function extractPrimaryText(result: ComputeResult | null): string {
  if (!result || !result.success) return '';
  const primary = result.pods.find(p =>
    ['Result', 'Results', 'Exact result', 'Solutions', 'Derivative',
      'Simplified Form', 'Factored Form', 'Expanded Form', 'Limit'].includes(p.title)
  ) || result.pods[1] || result.pods[0];
  return primary?.subpods?.[0]?.plaintext?.trim().toLowerCase().replace(/\s+/g, '') ?? '';
}

function judgeCorrect(userResult: ComputeResult | null, expectedResult: ComputeResult | null): boolean {
  if (!userResult?.success || !expectedResult?.success) return false;
  const u = extractPrimaryText(userResult);
  const e = extractPrimaryText(expectedResult);
  if (!u || !e) return false;
  if (u === e) return true;
  // numeric proximity check
  const uNum = parseFloat(u);
  const eNum = parseFloat(e);
  if (!isNaN(uNum) && !isNaN(eNum)) {
    return Math.abs(uNum - eNum) / (Math.abs(eNum) + 1e-9) < 0.01;
  }
  // partial match — if one contains the other (handles "x=3,x=4" vs "3,4")
  return u.includes(e) || e.includes(u);
}

function formatTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function shuffle<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

// ─── Setup Screen ─────────────────────────────────────────────────────────────

function SetupScreen({ onStart }: { onStart: (config: ExamConfig) => void }) {
  const [selectedSubjectId, setSelectedSubjectId] = useState<string>('algebra');
  const [count, setCount] = useState<number>(5);
  const [timeLimitMin, setTimeLimitMin] = useState<number>(10);
  const [difficulty, setDifficulty] = useState<string>('All');

  const subject = learningSubjects.find(s => s.id === selectedSubjectId)!;
  const difficulties = ['All', 'Beginner', 'Intermediate', 'Advanced'];

  const handleStart = () => {
    let pool = difficulty === 'All'
      ? subject.problems
      : subject.problems.filter(p => p.difficulty === difficulty);
    const picked = shuffle(pool).slice(0, Math.min(count, pool.length));
    onStart({
      subject,
      problems: picked,
      timeLimitSec: timeLimitMin === 0 ? 0 : timeLimitMin * 60,
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 to-fuchsia-600 px-8 py-6">
        <h2 className="text-2xl font-bold text-white mb-1">Exam Mode</h2>
        <p className="text-violet-100 text-sm">Configure your timed problem set and test your knowledge</p>
      </div>

      <div className="flex-1 overflow-y-auto p-8 space-y-8">
        {/* Subject Selector */}
        <div>
          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3">Subject</h3>
          <div className="grid grid-cols-3 gap-2">
            {learningSubjects.slice(0, 9).map(s => (
              <button
                key={s.id}
                onClick={() => setSelectedSubjectId(s.id)}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 text-sm font-medium transition-all cursor-pointer text-left ${
                  selectedSubjectId === s.id
                    ? 'border-violet-500 bg-violet-50 text-violet-700'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-violet-300'
                }`}
              >
                <div className={`w-7 h-7 flex items-center justify-center rounded-lg bg-gradient-to-br ${s.color} flex-shrink-0`}>
                  <i className={`${s.icon} text-white text-xs`}></i>
                </div>
                <span className="truncate">{s.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Difficulty Filter */}
        <div>
          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3">Difficulty Filter</h3>
          <div className="flex gap-2">
            {difficulties.map(d => (
              <button
                key={d}
                onClick={() => setDifficulty(d)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  difficulty === d
                    ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8">
          {/* Problem Count */}
          <div>
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3">Number of Problems</h3>
            <div className="grid grid-cols-4 gap-2">
              {[3, 5, 10, 15].map(n => (
                <button
                  key={n}
                  onClick={() => setCount(n)}
                  className={`py-3 rounded-xl text-sm font-bold transition-all cursor-pointer ${
                    count === n
                      ? 'bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white shadow-md'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          {/* Time Limit */}
          <div>
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3">Time Limit</h3>
            <div className="grid grid-cols-4 gap-2">
              {[5, 10, 20, 0].map(t => (
                <button
                  key={t}
                  onClick={() => setTimeLimitMin(t)}
                  className={`py-3 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    timeLimitMin === t
                      ? 'bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white shadow-md'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {t === 0 ? '∞' : `${t}m`}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Preview Summary */}
        <div className="bg-violet-50 border border-violet-200 rounded-2xl p-5">
          <h3 className="text-sm font-bold text-violet-800 mb-3 flex items-center gap-2">
            <i className="ri-survey-line"></i>
            Exam Preview
          </h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            {[
              { label: 'Problems', value: Math.min(count, subject.problems.length).toString() },
              { label: 'Time Limit', value: timeLimitMin === 0 ? 'Unlimited' : `${timeLimitMin} min` },
              { label: 'Subject', value: subject.name },
            ].map(({ label, value }) => (
              <div key={label} className="bg-white rounded-xl p-3">
                <div className="font-bold text-lg text-violet-700">{value}</div>
                <div className="text-xs text-violet-500">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="p-6 border-t border-gray-200">
        <button
          onClick={handleStart}
          className="w-full py-4 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white font-bold rounded-xl text-base shadow-lg hover:shadow-xl transition-all whitespace-nowrap cursor-pointer"
        >
          <i className="ri-timer-line mr-2"></i>
          Start Exam
        </button>
      </div>
    </div>
  );
}

// ─── Running Screen ───────────────────────────────────────────────────────────

function RunningScreen({
  config,
  onFinish,
}: {
  config: ExamConfig;
  onFinish: (answers: AnswerRecord[]) => void;
}) {
  const [idx, setIdx] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [lastResult, setLastResult] = useState<{ result: ComputeResult; correct: boolean } | null>(null);
  const [answers, setAnswers] = useState<AnswerRecord[]>([]);
  const [timeLeft, setTimeLeft] = useState(config.timeLimitSec);
  const problemStartRef = useRef<number>(Date.now());
  const answersRef = useRef<AnswerRecord[]>([]);
  const finishCalled = useRef(false);

  const currentProblem = config.problems[idx];
  const total = config.problems.length;

  const doFinish = useCallback((ans: AnswerRecord[]) => {
    if (finishCalled.current) return;
    finishCalled.current = true;
    // fill skipped remaining
    const remaining = config.problems.slice(ans.length).map(p => ({
      problemId: p.id,
      userInput: '',
      userResult: null,
      expectedResult: computeLocally(p.query),
      isCorrect: false,
      skipped: true,
      timeTakenSec: 0,
    }));
    onFinish([...ans, ...remaining]);
  }, [config.problems, onFinish]);

  // Timer countdown
  useEffect(() => {
    if (config.timeLimitSec === 0) return;
    const id = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearInterval(id);
          doFinish(answersRef.current);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [config.timeLimitSec, doFinish]);

  const timerPct = config.timeLimitSec > 0 ? (timeLeft / config.timeLimitSec) * 100 : 100;
  const timerColor = timerPct > 50 ? 'bg-emerald-500' : timerPct > 25 ? 'bg-amber-500' : 'bg-red-500';

  const handleSubmit = async () => {
    if (!userInput.trim()) return handleSkip();
    setIsChecking(true);
    await new Promise(r => setTimeout(r, 60));
    const userResult = computeLocally(userInput.trim());
    const expectedResult = computeLocally(currentProblem.query);
    const isCorrect = judgeCorrect(userResult, expectedResult);
    const timeTakenSec = Math.round((Date.now() - problemStartRef.current) / 1000);

    const record: AnswerRecord = {
      problemId: currentProblem.id,
      userInput: userInput.trim(),
      userResult,
      expectedResult,
      isCorrect,
      skipped: false,
      timeTakenSec,
    };

    const newAnswers = [...answersRef.current, record];
    answersRef.current = newAnswers;
    setAnswers(newAnswers);
    setLastResult({ result: userResult, correct: isCorrect });
    setIsChecking(false);
  };

  const handleNext = () => {
    setLastResult(null);
    setUserInput('');
    problemStartRef.current = Date.now();
    if (idx + 1 >= total) {
      doFinish(answersRef.current);
    } else {
      setIdx(i => i + 1);
    }
  };

  const handleSkip = () => {
    const expectedResult = computeLocally(currentProblem.query);
    const timeTakenSec = Math.round((Date.now() - problemStartRef.current) / 1000);
    const record: AnswerRecord = {
      problemId: currentProblem.id,
      userInput: '',
      userResult: null,
      expectedResult,
      isCorrect: false,
      skipped: true,
      timeTakenSec,
    };
    const newAnswers = [...answersRef.current, record];
    answersRef.current = newAnswers;
    setAnswers(newAnswers);
    setLastResult(null);
    setUserInput('');
    problemStartRef.current = Date.now();
    if (idx + 1 >= total) {
      doFinish(newAnswers);
    } else {
      setIdx(i => i + 1);
    }
  };

  const diffColor = (d: string) => d === 'Beginner' ? 'bg-emerald-100 text-emerald-700' : d === 'Intermediate' ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700';

  return (
    <div className="flex flex-col h-full">
      {/* Timer Header */}
      <div className="bg-gradient-to-r from-violet-600 to-fuchsia-600 px-8 py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 rounded-lg px-3 py-1.5 flex items-center gap-2">
              <i className="ri-question-mark text-white text-sm"></i>
              <span className="text-white font-bold text-sm">{idx + 1} / {total}</span>
            </div>
            <span className="text-violet-200 text-sm font-medium">{config.subject.name}</span>
          </div>
          {config.timeLimitSec > 0 ? (
            <div className={`flex items-center gap-2 px-4 py-1.5 rounded-full font-bold text-sm ${timeLeft <= 60 ? 'bg-red-500 text-white animate-pulse' : 'bg-white/20 text-white'}`}>
              <i className="ri-timer-line"></i>
              {formatTime(timeLeft)}
            </div>
          ) : (
            <div className="flex items-center gap-2 px-4 py-1.5 bg-white/20 rounded-full text-white text-sm font-bold">
              <i className="ri-infinity-line"></i>
              Unlimited
            </div>
          )}
        </div>
        {/* Progress bar */}
        <div className="w-full h-1.5 bg-white/20 rounded-full overflow-hidden">
          <div
            className="h-full bg-white transition-all duration-500 rounded-full"
            style={{ width: `${((idx) / total) * 100}%` }}
          ></div>
        </div>
        {config.timeLimitSec > 0 && (
          <div className={`w-full h-1 bg-white/10 rounded-full overflow-hidden mt-1.5`}>
            <div
              className={`h-full ${timerColor} transition-all duration-1000 rounded-full`}
              style={{ width: `${timerPct}%` }}
            ></div>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-8 space-y-6">
        {/* Problem Card */}
        <div className="bg-white border-2 border-violet-200 rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 flex-shrink-0 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center text-white font-bold">
              {idx + 1}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${diffColor(currentProblem.difficulty)}`}>
                  {currentProblem.difficulty}
                </span>
              </div>
              <p className="text-lg text-slate-800 font-medium leading-relaxed">{currentProblem.statement}</p>
            </div>
          </div>
        </div>

        {/* Answer Input */}
        {!lastResult && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <label className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
              <i className="ri-edit-2-line text-violet-500"></i>
              Your Answer / Query
            </label>
            <textarea
              value={userInput}
              onChange={e => setUserInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) handleSubmit(); }}
              placeholder={`e.g. ${currentProblem.query}`}
              rows={3}
              className="w-full mt-2 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-mono text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-400 resize-none"
            />
            <p className="text-xs text-slate-400 mt-2">Ctrl+Enter to submit</p>
          </div>
        )}

        {/* Result Feedback */}
        {lastResult && (
          <div className={`rounded-2xl p-6 border-2 ${lastResult.correct ? 'bg-emerald-50 border-emerald-400' : 'bg-red-50 border-red-400'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg ${lastResult.correct ? 'bg-emerald-500' : 'bg-red-500'}`}>
                <i className={lastResult.correct ? 'ri-check-line' : 'ri-close-line'}></i>
              </div>
              <div>
                <div className={`font-bold text-base ${lastResult.correct ? 'text-emerald-800' : 'text-red-800'}`}>
                  {lastResult.correct ? 'Correct!' : 'Incorrect'}
                </div>
                <div className="text-xs text-slate-500 font-mono mt-0.5">{userInput}</div>
              </div>
            </div>
            {lastResult.result.success && lastResult.result.pods[0] && (
              <div className="bg-white rounded-xl p-4 text-sm">
                <div className="text-xs font-semibold text-slate-500 mb-1">Engine output:</div>
                <MathRenderer
                  content={lastResult.result.pods[1]?.subpods?.[0]?.plaintext || lastResult.result.pods[0]?.subpods?.[0]?.plaintext || ''}
                  className="text-slate-800"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="p-6 border-t border-gray-200 flex gap-3">
        <button
          onClick={handleSkip}
          disabled={isChecking}
          className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-600 font-semibold rounded-xl transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
        >
          Skip
        </button>
        {lastResult ? (
          <button
            onClick={handleNext}
            className="flex-1 py-3 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white font-bold rounded-xl transition-all shadow-md whitespace-nowrap cursor-pointer"
          >
            {idx + 1 >= total ? 'See Results' : 'Next Problem'}
            <i className="ri-arrow-right-line ml-2"></i>
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={isChecking || !userInput.trim()}
            className="flex-1 py-3 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 disabled:from-slate-300 disabled:to-slate-400 text-white font-bold rounded-xl transition-all shadow-md whitespace-nowrap cursor-pointer disabled:cursor-not-allowed"
          >
            {isChecking ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Checking…
              </span>
            ) : (
              <span><i className="ri-check-double-line mr-2"></i>Check Answer</span>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Review / Scorecard Screen ────────────────────────────────────────────────

function ReviewScreen({
  config,
  answers,
  onRetry,
  onClose,
}: {
  config: ExamConfig;
  answers: AnswerRecord[];
  onRetry: () => void;
  onClose: () => void;
}) {
  const correct = answers.filter(a => a.isCorrect).length;
  const skipped = answers.filter(a => a.skipped).length;
  const attempted = answers.filter(a => !a.skipped).length;
  const totalTime = answers.reduce((s, a) => s + a.timeTakenSec, 0);
  const pct = Math.round((correct / answers.length) * 100);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const grade = pct >= 90 ? { label: 'A', color: 'text-emerald-600', bg: 'bg-emerald-100' }
    : pct >= 75 ? { label: 'B', color: 'text-teal-600', bg: 'bg-teal-100' }
    : pct >= 60 ? { label: 'C', color: 'text-amber-600', bg: 'bg-amber-100' }
    : { label: 'F', color: 'text-red-600', bg: 'bg-red-100' };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 to-fuchsia-600 px-8 py-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold">Exam Complete!</h2>
            <p className="text-violet-100 text-sm mt-0.5">{config.subject.name} · {config.problems.length} problems</p>
          </div>
          <div className={`w-16 h-16 ${grade.bg} rounded-2xl flex items-center justify-center`}>
            <span className={`text-3xl font-black ${grade.color}`}>{grade.label}</span>
          </div>
        </div>
        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'Score', value: `${correct}/${answers.length}` },
            { label: 'Accuracy', value: `${pct}%` },
            { label: 'Attempted', value: attempted.toString() },
            { label: 'Total Time', value: formatTime(totalTime) },
          ].map(({ label, value }) => (
            <div key={label} className="bg-white/15 rounded-xl p-3 text-center">
              <div className="font-bold text-lg">{value}</div>
              <div className="text-violet-200 text-xs">{label}</div>
            </div>
          ))}
        </div>
        {skipped > 0 && (
          <div className="mt-3 flex items-center gap-2 bg-white/10 rounded-lg px-3 py-2">
            <i className="ri-skip-forward-line text-violet-200"></i>
            <span className="text-violet-100 text-xs">{skipped} problem{skipped > 1 ? 's' : ''} skipped</span>
          </div>
        )}
      </div>

      {/* Score bar */}
      <div className="px-8 py-4 bg-slate-50 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="flex-1 h-3 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500 rounded-full transition-all duration-700"
              style={{ width: `${pct}%` }}
            ></div>
          </div>
          <span className="text-sm font-bold text-slate-700 w-10 text-right">{pct}%</span>
        </div>
      </div>

      {/* Problem Review List */}
      <div className="flex-1 overflow-y-auto p-6 space-y-3">
        <h3 className="text-sm font-bold text-slate-600 uppercase tracking-wide mb-4">Problem Breakdown</h3>
        {answers.map((ans, i) => {
          const prob = config.problems.find(p => p.id === ans.problemId)!;
          const isOpen = expandedId === ans.problemId;
          const expectedText = extractPrimaryText(ans.expectedResult);

          return (
            <div
              key={ans.problemId}
              className={`rounded-xl border-2 overflow-hidden transition-all ${
                ans.isCorrect ? 'border-emerald-300 bg-emerald-50'
                : ans.skipped ? 'border-slate-200 bg-slate-50'
                : 'border-red-300 bg-red-50'
              }`}
            >
              <button
                onClick={() => setExpandedId(isOpen ? null : ans.problemId)}
                className="w-full flex items-start gap-3 p-4 text-left cursor-pointer"
              >
                <div className={`w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                  ans.isCorrect ? 'bg-emerald-500' : ans.skipped ? 'bg-slate-400' : 'bg-red-500'
                }`}>
                  {ans.isCorrect ? <i className="ri-check-line"></i> : ans.skipped ? <i className="ri-skip-forward-line"></i> : <i className="ri-close-line"></i>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-slate-500">#{i + 1}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                      prob?.difficulty === 'Beginner' ? 'bg-emerald-100 text-emerald-700'
                      : prob?.difficulty === 'Intermediate' ? 'bg-amber-100 text-amber-700'
                      : 'bg-rose-100 text-rose-700'
                    }`}>{prob?.difficulty}</span>
                    <span className="text-xs text-slate-400 ml-auto">{formatTime(ans.timeTakenSec)}</span>
                  </div>
                  <p className="text-sm text-slate-800 font-medium line-clamp-2">{prob?.statement}</p>
                </div>
                <i className={`ri-arrow-${isOpen ? 'up' : 'down'}-s-line text-slate-400 flex-shrink-0`}></i>
              </button>

              {isOpen && (
                <div className="px-5 pb-5 space-y-3 border-t border-dashed border-slate-200 pt-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white rounded-xl p-3">
                      <div className="text-xs font-bold text-slate-500 mb-1.5 flex items-center gap-1">
                        <i className="ri-user-line text-slate-400"></i> Your Answer
                      </div>
                      <div className="text-sm font-mono text-slate-700">{ans.userInput || <em className="text-slate-400 not-italic">Skipped</em>}</div>
                    </div>
                    <div className="bg-white rounded-xl p-3">
                      <div className="text-xs font-bold text-slate-500 mb-1.5 flex items-center gap-1">
                        <i className="ri-check-double-line text-emerald-500"></i> Expected Answer
                      </div>
                      <MathRenderer content={expectedText || prob?.query} className="text-sm text-slate-800" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="p-6 border-t border-gray-200 flex gap-3">
        <button
          onClick={onClose}
          className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl transition-colors whitespace-nowrap cursor-pointer"
        >
          Close
        </button>
        <button
          onClick={onRetry}
          className="flex-1 py-3 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white font-bold rounded-xl shadow-md transition-all whitespace-nowrap cursor-pointer"
        >
          <i className="ri-restart-line mr-2"></i>
          Try Again
        </button>
      </div>
    </div>
  );
}

// ─── Main ExamMode ─────────────────────────────────────────────────────────────

export default function ExamMode({ onClose, onComplete }: ExamModeProps) {
  const [phase, setPhase] = useState<Phase>('setup');
  const [config, setConfig] = useState<ExamConfig | null>(null);
  const [answers, setAnswers] = useState<AnswerRecord[]>([]);

  const handleStart = (cfg: ExamConfig) => {
    setConfig(cfg);
    setAnswers([]);
    setPhase('running');
  };

  const handleFinish = (ans: AnswerRecord[]) => {
    setAnswers(ans);
    setPhase('review');
  };

  const handleRetry = () => {
    setAnswers([]);
    setPhase('setup');
  };

  const handleClose = () => {
    if (phase === 'review' && config && answers.length > 0 && onComplete) {
      const wrong = answers
        .filter(a => !a.isCorrect)
        .map(a => {
          const prob = config.problems.find(p => p.id === a.problemId);
          const correctText = extractPrimaryText(a.expectedResult) || prob?.query || '';
          return {
            statement: prob?.statement ?? a.problemId,
            userAnswer: a.userInput || '(skipped)',
            correctAnswer: correctText,
            subject: config.subject.name,
          };
        });
      if (wrong.length > 0) onComplete(wrong);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col relative">
        {/* Close button — always visible */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-white/30 hover:bg-white/50 transition-colors text-white cursor-pointer"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        {phase === 'setup' && <SetupScreen onStart={handleStart} />}
        {phase === 'running' && config && (
          <RunningScreen config={config} onFinish={handleFinish} />
        )}
        {phase === 'review' && config && (
          <ReviewScreen
            config={config}
            answers={answers}
            onRetry={handleRetry}
            onClose={handleClose}
          />
        )}
      </div>
    </div>
  );
}
